/*
 * Copyright (c) New Cloud Technologies, Ltd., 2013-2024
 *
 * You can not use the contents of the file in any way without New Cloud Technologies, Ltd. written permission.
 * To obtain such a permit, you should contact New Cloud Technologies, Ltd. at https://myoffice.ru/contacts/
 *
 */

import { DocumentFormats, DocumentTypes } from '@nct/wte-definitions/documentFormats';

type ValuesOf<T extends Record<any, any>> = T[keyof T];

const PDF_NAME = 'pdf';
export const DOCUMENT_TYPES = {
    [DocumentTypes.document]: DocumentTypes.document,
    [DocumentTypes.spreadsheet]: DocumentTypes.spreadsheet,
    [DocumentTypes.presentation]: DocumentTypes.presentation,
    [PDF_NAME]: PDF_NAME,
} as const;

export type DOCUMENT_TYPES_VALUES = ValuesOf<typeof DOCUMENT_TYPES>;

const XO_DOCUMENT_FORMAT = 'XO';
export const OPEN_FORMATS = {
    xo: XO_DOCUMENT_FORMAT,
    oxml: DocumentFormats.OXML,
    odf: DocumentFormats.ODF,
    plain: DocumentFormats.PLAIN_TEXT,
    dsv: DocumentFormats.DSV,
    pdf: DocumentFormats.PDF,
    pdfa: DocumentFormats.PDFA,
} as const;

export type OPEN_FORMATS_VALUES = ValuesOf<typeof OPEN_FORMATS>;

export const SAVE_FORMAT_NAMES = {
    [DocumentFormats.XO]: 'MyOffice Document',
    [DocumentFormats.ODF]: 'Open Document',
    [DocumentFormats.OXML]: 'Open XML',
    [DocumentFormats.PDF]: 'Pdf',
    [DocumentFormats.PDFA]: 'Pdfa',
} as const;

export const SAVE_FORMATS = {
    [DocumentFormats.XO]: DocumentFormats.XO,
    [DocumentFormats.ODF]: DocumentFormats.ODF, // формат OpenDocument. Документ будет сохранён в *.odt, *.ods или *.odp. При сохранении файла убедитесь, что у него правильное расширение или тип MIME.
    [DocumentFormats.OXML]: DocumentFormats.OXML, // формат Microsoft Office Open XML. Документ будет сохранён в *.docx, *.xlsx или *.pptx.
    [DocumentFormats.PDF]: DocumentFormats.PDF, // межплатформенный открытый формат электронных документов. Документ будет сохранён в *.pdf.
    [DocumentFormats.PDFA]: DocumentFormats.PDFA, // ISO-стандартизированный формат PDF/A для долгосрочного архивного хранения электронных документов. Документ будет сохранён в *.pdf.
    [DocumentFormats.DOCX]: DocumentFormats.DOCX,
    [DocumentFormats.ODT]: DocumentFormats.ODT,
    [DocumentFormats.XODT]: DocumentFormats.XODT,
    [DocumentFormats.DOC]: DocumentFormats.DOC,
    [DocumentFormats.DOT]: DocumentFormats.DOT,
    [DocumentFormats.DOTX]: DocumentFormats.DOTX,
    [DocumentFormats.DOCM]: DocumentFormats.DOCM,
    [DocumentFormats.RTF]: DocumentFormats.RTF,
    [DocumentFormats.XOTT]: DocumentFormats.XOTT,
    [DocumentFormats.OTT]: DocumentFormats.OTT,
    [DocumentFormats.TXT]: DocumentFormats.TXT,
    [DocumentFormats.DOTM]: DocumentFormats.DOTM,
    [DocumentFormats.XLS]: DocumentFormats.XLS,
    [DocumentFormats.XLT]: DocumentFormats.XLT,
    [DocumentFormats.XLTX]: DocumentFormats.XLTX,
    [DocumentFormats.XLSM]: DocumentFormats.XLSM,
    [DocumentFormats.XLTM]: DocumentFormats.XLTM,
    [DocumentFormats.CSV]: DocumentFormats.CSV,
    [DocumentFormats.XOTS]: DocumentFormats.XOTS,
    [DocumentFormats.OTS]: DocumentFormats.OTS,
    [DocumentFormats.TSV]: DocumentFormats.TSV,
    [DocumentFormats.PPTX]: DocumentFormats.PPTX,
    [DocumentFormats.ODP]: DocumentFormats.ODP,
    [DocumentFormats.XODP]: DocumentFormats.XODP,
    [DocumentFormats.PPT]: DocumentFormats.PPT,
    [DocumentFormats.POT]: DocumentFormats.POT,
    [DocumentFormats.POTX]: DocumentFormats.POTX,
    [DocumentFormats.PPS]: DocumentFormats.PPS,
    [DocumentFormats.PPSX]: DocumentFormats.PPSX,
    [DocumentFormats.PPSM]: DocumentFormats.PPSM,
    [DocumentFormats.PPTM]: DocumentFormats.PPTM,
    [DocumentFormats.XOTP]: DocumentFormats.XOTP,
    [DocumentFormats.OTP]: DocumentFormats.OTP,
} as const;

export type SAVE_FORMATS_VALUES = ValuesOf<typeof SAVE_FORMATS>;

export type FORMATS_VALUES = OPEN_FORMATS_VALUES | SAVE_FORMATS_VALUES;

// TODO Первым элементов в массиве ext должен быть extension, в котором хотим сохранять документы с определенным типом и форматом.
//  Этот костыль нужен для корректной работы getDocumentExtension. Будет исправлен при рефакторинге getDocumentExtension. WTE-36789
// Нужно также учесть, что это еще используется при открытии документа, чтобы получить type и format
export const STABLE_VARIANTS = [
    {
        type: DOCUMENT_TYPES.document,
        format: OPEN_FORMATS.oxml,
        ext: ['docx', 'dotx', 'docm'],
    },
    {
        type: DOCUMENT_TYPES.document,
        format: OPEN_FORMATS.odf,
        ext: ['odt', 'ott'],
    },
    {
        type: DOCUMENT_TYPES.document,
        format: OPEN_FORMATS.xo,
        ext: ['xodt', 'xott'],
    },
    {
        type: DOCUMENT_TYPES.document,
        format: OPEN_FORMATS.plain,
        ext: ['txt', 'log'],
    },
    {
        type: DOCUMENT_TYPES.spreadsheet,
        format: OPEN_FORMATS.oxml,
        ext: ['xlsx', 'xltx', 'xlsm'],
    },
    {
        type: DOCUMENT_TYPES.spreadsheet,
        format: OPEN_FORMATS.odf,
        ext: ['ods', 'ots'],
    },
    {
        type: DOCUMENT_TYPES.spreadsheet,
        format: OPEN_FORMATS.xo,
        ext: ['xods', 'xots'],
    },
    {
        type: DOCUMENT_TYPES.spreadsheet,
        format: OPEN_FORMATS.dsv,
        ext: ['csv', 'scsv', 'tsv', 'tab'],
    },
    {
        type: DOCUMENT_TYPES.pdf,
        format: OPEN_FORMATS.pdf,
        ext: ['pdf'],
    },
    {
        type: DOCUMENT_TYPES.presentation,
        format: OPEN_FORMATS.odf,
        ext: ['odp', 'otp'],
    },
    {
        type: DOCUMENT_TYPES.presentation,
        format: OPEN_FORMATS.xo,
        ext: ['xodp', 'xotp'],
    },
    {
        type: DOCUMENT_TYPES.presentation,
        format: OPEN_FORMATS.oxml,
        ext: ['pptx', 'potx', 'pptm'],
    },
];

export const AMR_API_VERSION = require('../package.json').version;
